import { ActionAfterViewObserver } from '@cian/action-after-viewed-component';
import {
  EButtonType,
  IBrokerData,
  NewbuildingBrokerContext,
  NewbuildingBrokerModalWindow,
} from '@cian/frontend-newbuilding-broker-component';
import * as React from 'react';
import { useSelector } from 'react-redux';

import { NewbuildingBrokerBanner, NewbuildingBrokerBannerButton } from '../../components/NewbuildingBrokerBanner';
import { getCurrentRegionId } from '../../selectors/getCurrentRegionId';
import { getJsonQuery } from '../../selectors/getJsonQuery';
import { getIsNeedSelfEmployedConfirmation } from '../../selectors/newbuildingBroker';
import { getNewbuildingBrokerSettings } from '../../selectors/newbuildingBroker/getNewbuildingBrokerSettings';
import {
  trackBannerClick,
  trackBannerShown,
  trackErrorModalOpen,
  trackNextOrGoButtonClick,
  trackRetryButtonClick,
  trackRewardInstructionButtonClick,
  trackRewardInstructionLinkClick,
} from '../../tracking/newbuilding/broker';
import { useApplicationContext } from '../../utils/applicationContext';

export const NewbuildingBrokerBannerContainer = () => {
  const { logger, httpApi } = useApplicationContext();

  const brokerContextValue = React.useMemo(() => ({ httpApi, logger }), [httpApi, logger]);

  const newbuildingBrokerSettings = useSelector(getNewbuildingBrokerSettings);
  const regionId = useSelector(getCurrentRegionId);
  const needsSelfEmployedConfirmation = useSelector(getIsNeedSelfEmployedConfirmation);
  const jsonQuery = useSelector(getJsonQuery);

  const handleBannerShown = React.useCallback(() => {
    trackBannerShown(jsonQuery);
  }, []);

  const handleModalOpenerClick = React.useCallback(() => {
    trackBannerClick(jsonQuery);
  }, []);

  const handleBrokerLinkFetched = React.useCallback((buttonType: EButtonType, result: IBrokerData | null) => {
    if (result) {
      trackNextOrGoButtonClick(jsonQuery, buttonType, result.brokerName);
    }
  }, []);

  const handleRewardInstructionButtonClick = React.useCallback(() => {
    trackRewardInstructionButtonClick(jsonQuery);
  }, []);

  const handleRewardInstructionLinkClick = React.useCallback(() => {
    trackRewardInstructionLinkClick(jsonQuery);
  }, []);

  const handleErrorModalOpen = React.useCallback(() => {
    trackErrorModalOpen(jsonQuery);
  }, []);

  const handleRetryButtonClick = React.useCallback(() => {
    trackRetryButtonClick(jsonQuery);
  }, []);

  return (
    <ActionAfterViewObserver callback={handleBannerShown} triggerOnce>
      <NewbuildingBrokerBanner
        button={
          <NewbuildingBrokerContext.Provider value={brokerContextValue}>
            <NewbuildingBrokerModalWindow
              renderModalOpener={NewbuildingBrokerBannerButton}
              newbuildingId={null}
              newbuildingRegionId={regionId}
              houseId={null}
              isSelfEmployed={needsSelfEmployedConfirmation}
              landingUrl={newbuildingBrokerSettings?.landingUrl}
              personalDataAgreementLink={newbuildingBrokerSettings?.personalDataAgreement}
              onModalOpenerClick={handleModalOpenerClick}
              onBrokerLinkFetched={handleBrokerLinkFetched}
              onRewardInstructionButtonClick={handleRewardInstructionButtonClick}
              onRewardInstructionLinkClick={handleRewardInstructionLinkClick}
              onErrorModalOpen={handleErrorModalOpen}
              onRetryButtonClick={handleRetryButtonClick}
            />
          </NewbuildingBrokerContext.Provider>
        }
      />
    </ActionAfterViewObserver>
  );
};
